import { useEffect } from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { Table } from "stories";
import { currencyStyle } from "utils/customHooks/currencyFormat";
import { v4 as uuidv4 } from "uuid";
import { getColumnTotal } from "../../../equityBreakdown";

export const processVacancyData = (data, prefixKey, isOnlyAssets = false) => {
  let processedData = {};
  if (
    (typeof data.price_psf !== "undefined" &&
      typeof data.total_price !== "undefined") ||
    typeof data !== "object"
  ) {
    return {
      [prefixKey]: isOnlyAssets
        ? data
        : typeof data.price_psf !== "undefined" &&
          typeof data.total_price !== "undefined"
        ? ""
        : data,
    };
  }

  for (const objKey of Object.keys(data)) {
    processedData = {
      ...processedData,
      ...processVacancyData(
        data[objKey],
        `${prefixKey ? prefixKey + ">" : ""}${objKey}`,
        isOnlyAssets
      ),
    };
  }
  return processedData;
};

export const generateTitle = (key) => {
  const keySections = key.split(">");
  if (keySections[1] === "other")
    return `${keySections[0]} ${keySections[1]} (${
      keySections[2] === "strata" ? "S" : "NS"
    }) Vacancy`;
  else
    return `${keySections[1]} (${
      keySections[2] === "strata" ? "S" : "NS"
    }) Vacancy`;
};

const headers = [
  {
    id: "title",
    title: "Reductions",
  },
  {
    id: "rate",
    title: "Vacancy Rate",
    align: "right",
  },
  {
    id: "amount",
    title: "Annual Reduction",
    align: "right",
  },
];

const operatingHeaders = [
  {
    id: "title",
    title: "Operating Expenses",
  },
  {
    id: "rate",
    title: "% Operating Revenue*",
    tooltip: "Default assumptions based on CMHC's Standardized Values",
    align: "right",
  },
  {
    id: "amount",
    title: "Annual Expenses",
    align: "right",
  },
];
export const ReductionsTable = ({
  netOperatingIncome,
  setNetOperatingIncome,
  estimatedAssetValue,
  setEstimatedAssetValue,
}) => {
  const form = useFormContext();
  const [
    capRate,
    assets,
    parking,
    storage,
    vacancyRates,
    parkingVacancyRate,
    storageVacancyRate,
    totalRevenue,
  ] = useWatch({
    name: [
      "cap_rate",
      "revenues.assets",
      "revenues.parking_assets",
      "revenues.storage_assets",
      "vacancy_rates",
      "parking_vacancy_rate",
      "storage_vacancy_rate",
      "total_revenue",
    ],
    control: form.control,
  });

  const {
    fields: operatingExpenses,
    append: addOperatingExpenses,
    remove: removeOperatingExpenses,
    update: updateOperatingExpenses,
  } = useFieldArray({
    name: "operating_expenses",
    control: form.control,
  });

  const {
    fields: reductions,
    append: addReduction,
    remove: removeReduction,
    update: updateReduction,
  } = useFieldArray({
    name: "reductions",
    control: form.control,
  });

  let vacancyArr = { ...vacancyRates };
  let assetsArr = { ...assets };

  vacancyArr = Object.keys(vacancyArr).map((key) => ({
    uid: `vacancy_rates.${key}`,
    title: generateTitle(key),
    rate: vacancyArr[key],
    amount: (row) => {
      const rate = Number(parseFloat(row.rate));
      if (rate && typeof assetsArr[key].total_price !== "undefined")
        return `$ ${currencyStyle(
          Number(
            parseFloat((-rate * assetsArr[key].total_price) / 100).toFixed(2)
          )
        )}`;
      return "";
    },
    props: {
      title: {
        className: "capitalize",
      },
      rate: {
        editable: true,
        type: "currency",
        prefix: "",
        suffix: "%",
        decimalsLimit: 2,
        decimalScale: 2,
      },
    },
  }));

  if (parking) {
    vacancyArr.push({
      uid: "parking_vacancy_rate",
      title: "Parking Vacancy",
      rate: parkingVacancyRate || "",
      amount: (row) => {
        const rate = Number(parseFloat(row.rate));
        if (rate && parking)
          return `$ (${currencyStyle(
            Number(parseFloat((rate * parking.total_price) / 100).toFixed(2))
          )})`;
        return "";
      },
      props: {
        rate: {
          editable: true,
          type: "currency",
          prefix: "",
          suffix: "%",
          decimalsLimit: 2,
          decimalScale: 2,
        },
      },
    });
  }
  if (storage) {
    vacancyArr.push({
      uid: "storage_vacancy_rate",
      title: "Storage Vacancy",
      rate: storageVacancyRate || "",
      amount: (row) => {
        const rate = Number(parseFloat(row.rate));
        if (rate && storage)
          return `$ (${currencyStyle(
            Number(parseFloat((rate * storage.total_price) / 100).toFixed(2))
          )})`;
        return "";
      },
      props: {
        rate: {
          editable: true,
          type: "currency",
          prefix: "",
          suffix: "%",
          decimalsLimit: 2,
          decimalScale: 2,
        },
      },
    });
  }

  reductions.forEach((row, i) => {
    vacancyArr.push({
      uid: i++,
      title: row.title,
      rate: "",
      amount: row.amount,
      props: {
        deletable: true,
        title: {
          editable: true,
        },
        amount: {
          editable: true,
          type: "currency",
          prefix: "$ (",
          suffix: ")",
        },
      },
    });
  });

  const onRowChanged = (rowIndex, cellId, value) => {
    const isVacancy = rowIndex <= vacancyArr.length - reductions.length - 1;
    if (isVacancy) {
      const row = vacancyArr[rowIndex];
      form.setValue(row.uid, value);
    } else {
      const i = rowIndex - (vacancyArr.length - reductions.length);
      updateReduction(i, {
        title: vacancyArr[rowIndex].title,
        amount: vacancyArr[rowIndex].amount,
        [cellId]: value,
      });
    }
  };

  const handleDelete = (rowIndex) => {
    const i = rowIndex - (vacancyArr.length - reductions.length);
    removeReduction(i);
  };

  const addNewRow = () => {
    addReduction({
      amount: "",
      title: "",
    });
  };

  const totalReductions = getColumnTotal(vacancyArr, "amount");
  const assetsFooter = [
    {
      uid: "assets-footer",
      title: "Total Reductions",
      rate: "",
      amount: () => {
        return `$ ${currencyStyle(totalReductions)}`;
      },
      props: {
        title: {
          colSpan: 2,
        },
      },
    },
  ];
  const totalOperationRevenueAmount = totalRevenue + totalReductions;
  const totalOperationRevenue = [
    {
      title: "Total Operating Revenue",
      rate: "",
      amount: () => {
        return `$ ${currencyStyle(
          Number(totalOperationRevenueAmount.toFixed(2))
        )}`;
      },
      props: {
        title: {
          colSpan: 2,
          className: "fw-bold",
        },
        amount: {
          className: "fw-bold",
        },
      },
    },
  ];

  const addNewOperation = () => {
    addOperatingExpenses({
      uid: uuidv4(),
      title: "",
      rate: "0",
      amount: "",
      props: {
        deletable: true,
        title: {
          editable: true,
        },
        rate: {
          editable: true,
          suffix: "%",
        },
      },
    });
  };
  operatingExpenses.forEach((row, i) => {
    row.uid = ++i;
    row.amount = (row) => {
      const rate = Number(parseFloat(row.rate));
      if (rate)
        return `$ ${currencyStyle(
          Number(
            parseFloat((-rate * totalOperationRevenueAmount) / 100).toFixed(2)
          )
        )}`;
      return "";
    };
    row.props = {
      ...row.props,
      deletable: i > 8,
      rate: {
        editable: true,
        type: "currency",
        prefix: "",
        suffix: "%",
        decimalsLimit: 2,
        decimalScale: 2,
      },
    };
  });

  const onOperatingRowChanged = (rowIndex, cellId, value) => {
    updateOperatingExpenses(rowIndex, {
      ...operatingExpenses[rowIndex],
      [cellId]: value,
    });
  };
  const opRate = getColumnTotal(operatingExpenses, "rate");
  const opTotal = getColumnTotal(operatingExpenses, "amount");
  const operatingExpensesFooter = [
    {
      title: "Total Operating Expenses",
      rate: opRate ? currencyStyle(opRate, 2) + "%" : "",
      amount: `$ ${currencyStyle(opTotal)}`,
    },
  ];

  const handleOperatingRowDelete = removeOperatingExpenses;
  const netOpIncome = Number(
    (totalOperationRevenueAmount + opTotal).toFixed(2)
  );
  const netOpIncomeFooter = [
    {
      title: "Net Operating Income",
      rate: "",
      amount: `$ ${currencyStyle(netOpIncome)}`,
      props: {
        title: {
          colSpan: 2,
        },
      },
    },
  ];
  useEffect(() => {
    if (netOperatingIncome !== netOpIncome) {
      setNetOperatingIncome(netOpIncome);
    }
  }, [netOperatingIncome, setNetOperatingIncome, netOpIncome]);
  const capRateRows = [
    {
      title: "Cap Rate",
      rate: "",
      amount: capRate,
      props: {
        title: {
          colSpan: 2,
        },
        amount: {
          editable: true,
          type: "currency",
          prefix: "",
          suffix: "%",
        },
      },
    },
  ];
  const eav =
    capRate && (netOpIncome * 100) / capRate !== Infinity
      ? Number(((netOpIncome * 100) / capRate).toFixed(2))
      : "-";
  const estimatedAssetsFooter = [
    {
      title: "Estimated Asset Value",
      rate: "",
      amount: `$ ${currencyStyle(eav)}`,
      props: {
        title: {
          colSpan: 2,
        },
      },
    },
  ];

  useEffect(() => {
    if (eav !== estimatedAssetValue) setEstimatedAssetValue(eav);
  }, [eav, estimatedAssetValue, setEstimatedAssetValue]);

  const onCapRateChanged = (rowItem, cellId, value) => {
    form.setValue("cap_rate", value);
  };

  return (
    <div className="pr-2/5">
      <div className="mb-2">
        <Table
          sameColumnWidth
          headers={headers}
          rows={vacancyArr}
          footer={assetsFooter}
          onFieldChanged={onRowChanged}
          onDelete={handleDelete}
        />

        <div
          className={"fw-bold text-primary mb-3 d-inline-block"}
          role={"button"}
          onClick={addNewRow}
        >
          + Add reduction
        </div>

        <Table
          sameColumnWidth
          hideHeader
          headers={headers}
          rows={totalOperationRevenue}
          footer={[]}
        />
      </div>
      <div className="mb-2 mt-4">
        <Table
          sameColumnWidth
          headers={operatingHeaders}
          rows={operatingExpenses}
          footer={operatingExpensesFooter}
          onFieldChanged={onOperatingRowChanged}
          onDelete={handleOperatingRowDelete}
        />

        <div
          className={"fw-bold text-primary mb-3 d-inline-block"}
          role={"button"}
          onClick={addNewOperation}
        >
          + Add operating expense
        </div>
      </div>

      <div className="mb-2 mt-4">
        <Table
          hideHeader
          sameColumnWidth
          headers={operatingHeaders}
          rows={[]}
          footer={netOpIncomeFooter}
        />
      </div>

      <div className="mb-2 mt-4">
        <Table
          hideHeader
          sameColumnWidth
          headers={operatingHeaders}
          rows={capRateRows}
          footer={estimatedAssetsFooter}
          onFieldChanged={onCapRateChanged}
        />
      </div>
    </div>
  );
};
